import * as z from "zod";
import { triggerStepSchema } from "./advanced-flow-db-parser";

export interface TriggerRef {
  category: string;
  key: string;
}

function literalValues(schema: unknown): string[] {
  if (schema instanceof z.ZodLiteral) {
    return [...schema.values].map(String);
  }
  if (schema instanceof z.ZodEnum) {
    return schema.options.map(String);
  }
  return [];
}

function refId(ref: TriggerRef): string {
  return `${ref.category}\u0000${ref.key}`;
}

/** Walks the trigger step union so a trigger gaining fields is picked up here unchanged. */
function collect(
  schema: z.core.$ZodType,
  into: Map<string, StepTrigger>,
): void {
  if (schema instanceof z.ZodUnion) {
    for (const option of schema.options) {
      collect(option, into);
    }
    return;
  }
  if (schema instanceof z.ZodPipe) {
    collect(schema.out, into);
    return;
  }
  if (!(schema instanceof z.ZodObject)) {
    return;
  }
  const fields = schema.shape.triggerSpecificFields;
  for (const category of literalValues(schema.shape.category)) {
    for (const key of literalValues(schema.shape.key)) {
      const id = refId({ category, key });
      if (!into.has(id)) {
        into.set(id, {
          trigger: { category, key },
          fields: fields instanceof z.ZodType ? fields : null,
        });
      }
    }
  }
}

interface StepTrigger {
  trigger: TriggerRef;
  fields: z.ZodType | null;
}

const stepTriggers = new Map<string, StepTrigger>();
collect(triggerStepSchema, stepTriggers);

/** Every trigger the walk found in the step schema, so tests can pin which ones carry fields. */
export function triggerStepSchemaTriggers(): readonly StepTrigger[] {
  return [...stepTriggers.values()];
}

/** The stored `triggerSpecificFields` schema for a trigger. Null when it has none. */
export function triggerSpecificFieldsSchema(
  trigger: TriggerRef,
): z.ZodType | null {
  return stepTriggers.get(refId(trigger))?.fields ?? null;
}

export function triggerSpecificFieldsJsonSchema(
  trigger: TriggerRef,
): Record<string, unknown> | null {
  const schema = triggerSpecificFieldsSchema(trigger);
  return schema == null
    ? null
    : z.toJSONSchema(schema, { io: "input", unrepresentable: "any" });
}
